
import React, { useState, useEffect, useRef } from 'react';
import { PageContainer } from '@/components/layout/PageContainer';
import { Card } from '@/components/ui/card';
import {
  Clock, DollarSign, CalendarCheck, ChevronRight,
  TrendingUp, Users, MapPin, ChevronLeft, Calendar as CalendarIcon
} from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cardRef.current) {
      console.log(
        "[Dashboard.tsx] Card element fontFamily:",
        getComputedStyle(cardRef.current).fontFamily
      );
    }
  }, []);

  const [date, setDate] = useState<Date>(new Date());

  // Mock data for demonstration
  const events = [
    {
      id: 1,
      title: 'Annual Conference',
      client: 'Acme Corp',
      venue: 'Grand Hotel',
      startTime: '09:00 AM',
      endTime: '05:00 PM',
      date: new Date(2023, 10, 15)
    },
    {
      id: 2,
      title: 'Product Launch',
      client: 'TechStart',
      venue: 'Innovation Center',
      startTime: '10:00 AM',
      endTime: '02:00 PM',
      date: new Date(2023, 10, 18)
    },
    {
      id: 3,
      title: 'Team Building',
      client: 'Global Systems',
      venue: 'Adventure Park',
      startTime: '01:00 PM',
      endTime: '06:00 PM',
      date: new Date()
    }
  ];

  // Filter events for the selected date
  const selectedDateEvents = events.filter(event =>
    event.date.getDate() === date.getDate() &&
    event.date.getMonth() === date.getMonth() &&
    event.date.getFullYear() === date.getFullYear()
  );

  const upcomingBookings = [
    { id: 1, client: 'Acme Corp', event: 'Annual Conference', date: '2023-11-15', venue: 'Grand Hotel' },
    { id: 2, client: 'TechStart', event: 'Product Launch', date: '2023-11-18', venue: 'Innovation Center' },
  ];

  const stats = [
    { label: 'This Month', value: '$4,250', change: '+12%', icon: DollarSign },
    { label: 'Active Bookings', value: '8', change: '+2', icon: Calendar },
    { label: 'Pending Invoices', value: '3', change: '-1', icon: CalendarCheck },
  ];

  return (
    <PageContainer title="Dashboard" subtitle="Welcome back">
      <div className="space-y-6">
        {/* Sign Out Button */}
        <div className="flex justify-end">
          <Button 
            variant="outline" 
            onClick={() => {
              signOut().catch(error => {
                console.error('Sign out error:', error);
              });
            }}
          >
            Sign Out
          </Button>
        </div>

        {/* Stats Section */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {stats.map((stat, index) => (
            <Card key={index} ref={index === 0 ? cardRef : undefined} className="p-4 hover-lift">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{stat.label}</p>
                  <h3 className="text-2xl font-bold tracking-tight mt-1">{stat.value}</h3>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center">
                    <TrendingUp className="w-3 h-3 mr-1 text-green-500" />
                    <span>{stat.change} from last month</span>
                  </p>
                </div>
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <stat.icon className="h-5 w-5" />
                </div>
              </div>
            </Card>
          ))}
        </section>

        {/* Upcoming Bookings */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold tracking-tight">Upcoming Bookings</h2>
            <button className="text-sm text-primary flex items-center">
              View all <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>

          <div className="space-y-3">
            {upcomingBookings.map((booking) => (
              <Card
                key={booking.id}
                className="p-4 animate-slide-in-bottom touch-feedback active:scale-98"
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <h3 className="font-medium">{booking.event}</h3>
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Users className="h-3.5 w-3.5 mr-1.5" />
                      <span>{booking.client}</span>
                    </div>
                    <div className="flex items-center text-sm text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5 mr-1.5" />
                      <span>{booking.venue}</span>
                    </div>
                  </div>

                  <div className="flex flex-col items-end">
                    <div className="flex items-center text-sm font-medium">
                      <Calendar className="h-3.5 w-3.5 mr-1.5" />
                      <span>{new Date(booking.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })}</span>
                    </div>
                    <div className="flex items-center text-sm text-muted-foreground mt-1">
                      <Clock className="h-3.5 w-3.5 mr-1.5" />
                      <span>9:00 AM - 5:00 PM</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* Calendar Section */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-4 md:col-span-2">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Booking Calendar</h2>
            </div>

            <Calendar
              mode="single"
              selected={date}
              onSelect={(newDate: Date | undefined) => newDate && setDate(newDate)}
              className="mx-auto rounded-md border"
              classNames={{
                day_today: "bg-accent text-accent-foreground font-bold border border-primary"
              }}
            />
          </Card>

          <div className="space-y-4">
            <Card className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">
                  {date ? format(date, 'EEEE, MMMM do, yyyy') : 'Select a date'}
                </h2>
              </div>

              {selectedDateEvents.length > 0 ? (
                <div className="space-y-3">
                  {selectedDateEvents.map(event => (
                    <Card key={event.id} className="p-3 hover:shadow-md transition-shadow">
                      <h3 className="font-medium text-primary">{event.title}</h3>
                      <div className="mt-2 space-y-1 text-sm">
                        <div className="flex items-center text-muted-foreground">
                          <Users className="h-3.5 w-3.5 mr-2" />
                          <span>{event.client}</span>
                        </div>
                        <div className="flex items-center text-muted-foreground">
                          <MapPin className="h-3.5 w-3.5 mr-2" />
                          <span>{event.venue}</span>
                        </div>
                        <div className="flex items-center text-muted-foreground">
                          <Clock className="h-3.5 w-3.5 mr-2" />
                          <span>{event.startTime} - {event.endTime}</span>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <CalendarIcon className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p>No events scheduled for this date</p>
                  <Button className="mt-4" variant="outline" size="sm">
                    Create Event
                  </Button>
                </div>
              )}
            </Card>
          </div>
        </section>

        {/* Quick Actions */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold tracking-tight">Quick Actions</h2>

          <div className="grid grid-cols-2 gap-3">
            <Card className="p-4 hover-lift touch-feedback">
              <div className="flex flex-col items-center justify-center text-center space-y-2">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <Calendar className="h-5 w-5" />
                </div>
                <h3 className="font-medium">New Booking</h3>
                <p className="text-xs text-muted-foreground">Create a new booking</p>
              </div>
            </Card>

            <Card className="p-4 hover-lift touch-feedback">
              <div className="flex flex-col items-center justify-center text-center space-y-2">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <DollarSign className="h-5 w-5" />
                </div>
                <h3 className="font-medium">New Invoice</h3>
                <p className="text-xs text-muted-foreground">Generate an invoice</p>
              </div>
            </Card>
          </div>
        </section>
      </div>
    </PageContainer>
  );
};

export default Dashboard;
